import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../../services/ApiService';
import { eventBasePathFromLocation } from '../../utils/eventSlug';
import PageLoader from '../ui/PageLoader';
import Footer from '../Footer';
import Button from '../ui/Button';
import PageBackLink from '../ui/PageBackLink';

const POLL_MS = 2500;
const MAX_POLLS = 24;

type TicketPreview = {
  id: number;
  status: string;
  token?: string | null;
  ticket_type_name?: string | null;
};

const SpectatorPaymentReturnPage: React.FC = () => {
  const location = useLocation();
  const eventBasePath = eventBasePathFromLocation(location.pathname);
  const ticketsPath = `${eventBasePath}/tickets`;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const orderIdParam = searchParams.get('order_id');
  const orderId = orderIdParam ? Number(orderIdParam) : 0;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [label, setLabel] = useState('Confirming payment');
  const [confirmed, setConfirmed] = useState(false);
  const [tickets, setTickets] = useState<TicketPreview[]>([]);
  const [pollCount, setPollCount] = useState(0);

  const poll = useCallback(async () => {
    if (!sessionId && !(orderId > 0)) {
      setError('Missing payment reference');
      setLoading(false);
      return;
    }
    if (!apiService.isAuthenticated()) {
      setError('Sign in to view your tickets.');
      setLoading(false);
      return;
    }
    try {
      const data = await apiService.getSpectatorPaymentStatus({
        session_id: sessionId || undefined,
        order_id: orderId > 0 ? orderId : undefined,
      });
      setLabel(data.label);
      if (data.confirmed) {
        setConfirmed(true);
        setTickets((data.tickets || []) as TicketPreview[]);
        setLoading(false);
        return;
      }
      if (data.failed) {
        setLoading(false);
        setError('Payment did not complete. You can try again from the ticket page.');
        return;
      }
      setPollCount((c) => c + 1);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not confirm payment');
      setLoading(false);
    }
  }, [sessionId, orderId]);

  useEffect(() => {
    void poll();
  }, [poll]);

  useEffect(() => {
    if (confirmed || error || pollCount >= MAX_POLLS) {
      if (pollCount >= MAX_POLLS && !confirmed && !error) {
        setLoading(false);
        setError(
          'Payment is still processing. Open My tickets in a moment. If tickets never appear, contact support with your order details.'
        );
      }
      return undefined;
    }
    const timer = window.setTimeout(() => {
      void poll();
    }, POLL_MS);
    return () => window.clearTimeout(timer);
  }, [poll, pollCount, confirmed, error]);

  if (loading && !error && !confirmed) {
    return (
      <>
        <PageLoader message="Confirming ticket payment…" />
        <Footer />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <div className="flex-1 max-w-lg mx-auto w-full px-4 py-12 text-center">
        <PageBackLink
          label="Back to event"
          to={eventBasePath}
          className="mb-6 justify-center"
        />

        {confirmed ? (
          <>
            <p className="kicker mb-3">Payment confirmed</p>
            <h1 className="heading-section mb-3">Tickets ready</h1>
            <p className="text-muted text-sm mb-6 leading-relaxed">
              Show these codes at the gate. Email delivery is not available — save them here or in My
              tickets.
            </p>
            {tickets.length > 0 ? (
              <ul className="space-y-3 mb-6 text-left">
                {tickets.map((ticket) => (
                  <li
                    key={ticket.id}
                    className="rounded-xl border border-orange-500/30 bg-orange-950/20 p-4"
                  >
                    <p className="text-sm text-gray-300 mb-2">
                      {ticket.ticket_type_name || 'Ticket'} · {ticket.status}
                    </p>
                    {ticket.token ? (
                      <p className="font-mono text-sm break-all text-orange-100" aria-label="Ticket code">
                        {ticket.token}
                      </p>
                    ) : (
                      <p className="text-xs text-gray-500">Code available in My tickets</p>
                    )}
                  </li>
                ))}
              </ul>
            ) : null}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button variant="primary" onClick={() => navigate('/tickets')} className="min-h-[44px]">
                View all tickets
              </Button>
              {eventBasePath !== '/events' ? (
                <Link
                  to={eventBasePath}
                  className="btn-ghost min-h-[44px] inline-flex items-center justify-center px-4"
                >
                  Back to event
                </Link>
              ) : null}
            </div>
          </>
        ) : (
          <>
            <p className="kicker mb-3">Spectator tickets</p>
            <h1 className="heading-section mb-3">{label}</h1>
            <p className="text-muted text-sm mb-6 leading-relaxed">
              {error ||
                'We are waiting for confirmation. Do not close this page yet if you just finished paying.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {!error ? (
                <Button variant="secondary" loading={loading} onClick={() => void poll()} className="min-h-[44px]">
                  Check again
                </Button>
              ) : null}
              <Link
                to="/tickets"
                className="btn-primary min-h-[44px] inline-flex items-center justify-center px-4"
              >
                Open My tickets
              </Link>
              {eventBasePath !== '/events' ? (
                <Link
                  to={ticketsPath}
                  className="btn-ghost min-h-[44px] inline-flex items-center justify-center px-4"
                >
                  Ticket page
                </Link>
              ) : null}
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default SpectatorPaymentReturnPage;
